// this is a rough copy of the feature flag store from the global website
// Ideally booking endine should be upgraded to use zustand or something easier to work with
// this store will save the feature flag values fetched from config cat, or provide default values for the flags


// Data types
//========================================================================
     
export enum FlagSource {
    Default = 'default', // flag came from the default hard-coded values
    Restored = 'restored', // flag was restored from previous session storage
    Fresh = 'fresh', // flag was loaded directly from config cat
    Override = 'override' // flag was manually toggled by dev / qa using ui debug widget 
}

export interface Flag {
    enabled: boolean;
    source: FlagSource;
    venueId: number | undefined;
}

export interface FlagBehaviour {
    sticky: boolean;
    venueAffected: boolean;
}

//list of flags by flag alias
export type FlagList = Record<string, Flag>;

//list of flags by flag alias, grouped by venue id
export type FlagVenueList = Record<string, Record<number, Flag>>;


// Initial/default flag values
//========================================================================

export enum KnownFlag {
    recentlySoldCounts = 'recentlySoldCounts',
    playerCountUpsell = 'playerCountUpsell',
    blackFriday = 'blackFriday',
}

const knownFlagBehaviour: Record<KnownFlag, FlagBehaviour> = {
    [KnownFlag.recentlySoldCounts]: { sticky: false, venueAffected: true },
    [KnownFlag.playerCountUpsell]: { sticky: false, venueAffected: false },
    [KnownFlag.blackFriday]: { sticky: false, venueAffected: true }
};

export const initialFlagValues: FlagList = {
    [KnownFlag.recentlySoldCounts]: { enabled: false, source: FlagSource.Default, venueId: undefined },
    [KnownFlag.playerCountUpsell]: { enabled: false, source: FlagSource.Default, venueId: undefined },
    [KnownFlag.blackFriday]: { enabled: false, source: FlagSource.Default, venueId: undefined }
};

// Offer shown while the blackFriday flag is on; update each year to match the live promo code.
export const blackFridayPromo = { percentage: 25, code: 'BF25' };


// Data structure for the store
//========================================================================

export interface FeatureFlagState {
    flags: FlagList; // used for current behaviour
    flagVenueSettings: FlagVenueList; //used for tracking venue chang and sticky settings accross sessions
    loading: boolean;
    hasLoaded: boolean;
    abandoned: boolean;
    lastLoadedVenueId: number | undefined;
    userSessionId: string | undefined // random unique id for this user device / browser
}


// Action types and interfaces
//========================================================================
export const SET_FLAGS = 'SET_FLAGS';
export const SET_FLAG = 'SET_FLAG';
export const RESET_FLAGS = 'RESET_FLAGS';
export const SET_LOADING = 'SET_LOADING';
export const SET_HAS_LOADED = 'SET_HAS_LOADED';
export const SET_ABANDONED = 'SET_ABANDONED';
export const SET_USER_SESSION_ID = 'SET_USER_SESSION_ID';

interface SetFlagsAction {
    type: typeof SET_FLAGS;
    payload: FlagList;
    venueId: number | undefined;
}

interface SetFlagAction {
    type: typeof SET_FLAG;
    key: string;
    newFlag: Flag;
}

interface ResetFlagsAction {
    type: typeof RESET_FLAGS;
}

interface SetLoadingAction {
    type: typeof SET_LOADING;
    payload: boolean;
}

interface SetHasLoadedAction {
    type: typeof SET_HAS_LOADED;
    payload: boolean;
}

interface SetAbandonedAction {
    type: typeof SET_ABANDONED;
    payload: boolean;
}

interface SetUserSessionIdAction {
    type: typeof SET_USER_SESSION_ID;
    payload: string;
}

export type FeatureFlagActions =
    | SetFlagsAction
    | SetFlagAction
    | ResetFlagsAction
    | SetLoadingAction
    | SetHasLoadedAction
    | SetAbandonedAction
    | SetUserSessionIdAction;



// Action creators
//========================================================================

export const featureFlagActions = {
    setFlags: (payload: FlagList, venueId: number | undefined): SetFlagsAction => ({
        type: SET_FLAGS,
        payload,
        venueId
    }),

    setFlag: (key: string, newFlag: Flag): SetFlagAction => ({
        type: SET_FLAG,
        key,
        newFlag
    }),

    resetFlags: (): ResetFlagsAction => ({
        type: RESET_FLAGS,
    }),

    setLoading: (loading: boolean): SetLoadingAction => ({
        type: SET_LOADING,
        payload: loading,
    }),

    setHasLoaded: (loaded: boolean): SetHasLoadedAction => ({
        type: SET_HAS_LOADED,
        payload: loaded,
    }),

    setAbandoned: (abandoned: boolean): SetAbandonedAction => ({
        type: SET_ABANDONED,
        payload: abandoned,
    }),

    setUserSessionId: (userSessionId: string): SetUserSessionIdAction => ({
        type: SET_USER_SESSION_ID,
        payload: userSessionId,
    }),
};

// Reducer
//========================================================================
const initialState: FeatureFlagState = {
    flags: { ...initialFlagValues },
    flagVenueSettings: {},
    loading: false,
    hasLoaded: false,
    abandoned: false,
    lastLoadedVenueId: undefined,
    userSessionId: undefined
};

const stickyFlagPriority = {
    [FlagSource.Default]: 0,
    [FlagSource.Fresh]: 1,
    [FlagSource.Restored]: 2,   // highest normal action, 
    [FlagSource.Override]: 3,
} as const;

const volatileFlagPriority = {
    [FlagSource.Restored]: 0, // assumed stale and worse than nothing
    [FlagSource.Default]: 1,
    [FlagSource.Fresh]: 2,   // highest normal action
    [FlagSource.Override]: 3,
} as const;

// creates a random string id that can be used to identify the current browser session, or a session restored from this on within the local storate cache period
// used to seed the config cat sdk so it buckets users properly when % based splits are used
// (bucketing is done determanistically on the front end so user data doesn't leave the browser)
export function createUserSessionId() {

        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            // Modern browsers
            const id = crypto.randomUUID();
            console.log(`Taging user session using crypto.randomUUID returned: ${id}`);
            return id;

        } else if (typeof crypto !== "undefined" && crypto.getRandomValues) {
            // Older browsers with Web Crypto core API
            const array = new Uint8Array(1);
            const template = '10000000-1000-4000-8000-100000000000';

            const id = template.replace(/[018]/g, char => {
                crypto.getRandomValues(array);
                return (
                    Number(char) ^
                    ((array[0] & 0x0f) >> (Number(char) / 4))
                ).toString(16);

            });

            console.log(`Taging user session using crypto.getRandomValues returned: ${id}`);
            return id;

        } else {
            // Legacy fallback - not as random/unique
            const id = 'fallback-' + Date.now() + '-' + Math.floor(Math.random() * 1e9);
            console.log(`Taging user session using fallback returned: ${id}`);
            return id;
        }
}


function flagShouldOverride(
    behaviour: FlagBehaviour | undefined,
    current: Flag | undefined,
    incoming: Flag,
    lastLoadedVenueId: number | undefined
): boolean {
    // indicates if a new flag value should take priority over a previously set value.
    // helps coordinate between default values, values restored from session storage, and fresh values from config cat.
    // also check for venue context changes on affected flags

    //if we don't have a current value, use the new one
    if (!current) return true;

    if (behaviour) {

        // if the new flag is affected by venue context, and we have a context we are aiming for
        if (behaviour.venueAffected && lastLoadedVenueId) {
            // incoming request is for the correct context, but the old flag is for a different context
            if (lastLoadedVenueId === incoming.venueId && current.venueId !== incoming.venueId) {
                //use whatever the new flag is
                return true;
            }
        }

        //sticky flags should prioratise previously saved values
        const table = behaviour.sticky ? stickyFlagPriority : volatileFlagPriority;
        return table[incoming.source] >= table[current.source];
    }

    // if no behaviour is configured assume it's a volitile flag
    return volatileFlagPriority[incoming.source] >= volatileFlagPriority[current.source];
}

export const flagToString = (x: Flag): string => {
    if (x) {
        return `[enabled:${x.enabled},source:${x.source},venue:${x.venueId}]`;
    }

    return `[flag-empty]`;
}

function applyFlagUpdate(
    state: FeatureFlagState,
    flagId: string,
    newFlag: Flag,
    venueId: number | undefined
): FeatureFlagState {
    // applying flag updates has 2 considerations
    // what source does the flag prioratize (fresh from config cat every time or sticky from saved state every time?)
    // is the flag affected by the venue context

    //console.log(`feature flag store - recieved new flag request - ${flagToString(newFlag)}`);

    // see if we have pre configured behaviour for this flag
    const behaviour = getFlagBehaviour(flagId);
    let changed = false;

    let flagData = state.flags;
    let venueSettingsData = state.flagVenueSettings;

    let isVenueAffected = (behaviour && behaviour.venueAffected);
    const currentFlag = state.flags[flagId];
    //console.log(`current flag for comparison : ${flagToString(currentFlag)}`);

    // if the flag is venue affected, and for a different venue, see if we are better grabbing the one from storage
    if (isVenueAffected) {
        if (newFlag.venueId && (newFlag.venueId !== (currentFlag?.venueId ?? undefined))) {

            //compare the new flag with the value in storage to pick the priority one
            const previousVenueFlag = state.flagVenueSettings[flagId]?.[newFlag.venueId];
            //console.log(`checking new flag ${flagToString(newFlag)} against saved venue setting for that venue ${flagToString(previousVenueFlag)}`);

            if (!flagShouldOverride(behaviour, previousVenueFlag, newFlag, venueId)) {
                //the one from storage is higher priority, swap that into the active slot
                //console.log(`new flag changed venue context, but a better value was found in the store, using that insted`);
                newFlag = previousVenueFlag;
            }
        }
    }
    else {
        //clear the venue ids off flags that don't need them
        newFlag.venueId = undefined;
    }

    // if the incoming flag is valid and higher priority use it
    if (flagShouldOverride(behaviour, state.flags[flagId], newFlag, venueId)) {
        //console.log(`using new value for flag ${flagId} - ${flagToString(newFlag)} over old value ${flagToString(state.flags[flagId])}`);
        flagData = { ...state.flags, [flagId]: newFlag };
        changed = true;
    }

    // if venue context matters, save a copy into the flagVenueSettings list, and mark it as restored to give it higher priority if we need to pull it back out
    if (behaviour && behaviour.venueAffected && newFlag.venueId) {
        const venueSetting = state.flagVenueSettings[flagId]?.[newFlag.venueId];

        if (flagShouldOverride(behaviour, venueSetting, newFlag, venueId)) {
            //console.log(`using new value for venue setting, flag ${flagId} - ${flagToString(newFlag)} over old value ${flagToString(state.flags[flagId])}`);
            venueSettingsData = {
                ...state.flagVenueSettings,
                [flagId]: {
                    ...(state.flagVenueSettings[flagId] ?? {}),
                    [newFlag.venueId]: { ...newFlag, source: FlagSource.Restored }
                }
            };
            changed = true;
        }
    }

    //if anything changed update the sate
    if (changed) {
        return {
            ...state, flags: flagData, flagVenueSettings: venueSettingsData
        }
    }

    //otherwise ignore the request and use the existing state
    return state;
}


export const featureFlagReducer = (
    state = initialState,
    action: FeatureFlagActions
): FeatureFlagState => {
    switch (action.type) {
        case SET_FLAGS:
            {
                // for each flag in the payload, just loop thru the same set flag logic as the set single flag action
                // accumulating all state changes before returning 
                let nextState = state;
                nextState.lastLoadedVenueId = action.venueId;
                for (const [flagId, flag] of Object.entries(action.payload)) {
                    nextState = applyFlagUpdate(nextState, flagId, flag, action.venueId);
                }
                return nextState;
            }

        case SET_FLAG:
            {
                const { key: flagId, newFlag } = action;
                return applyFlagUpdate(state, flagId, newFlag, state.lastLoadedVenueId);
            }

        case RESET_FLAGS:
            return {
                ...state,
                flags: { ...initialFlagValues },
            };

        case SET_LOADING:
            return {
                ...state,
                loading: action.payload,
            };

        case SET_HAS_LOADED:
            return {
                ...state,
                hasLoaded: action.payload,
            };

        case SET_ABANDONED:
            return {
                ...state,
                abandoned: action.payload,
            };

        case SET_USER_SESSION_ID:
            return {
                ...state,
                userSessionId : action.payload,
            };

        default:
            return state;
    }
};


// Selectores / helpers
//========================================================================
export const isFeatureEnabled = (state: FeatureFlagState, key: string) => {
    return state.flags[key]?.enabled ?? false;
}


// Flags not in KnownFlag are loaded from ConfigCat to hide Contentful booking alerts tagged with them, which can be targeted by venue.
const unknownFlagBehaviour: FlagBehaviour = { sticky: false, venueAffected: true };

export function getFlagBehaviour(flagId: string): FlagBehaviour {
    if (Object.values(KnownFlag).includes(flagId as KnownFlag)) {
        return knownFlagBehaviour[flagId as KnownFlag];
    }
    return unknownFlagBehaviour;
}